// Angular or Core
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
// Service
import { AppPermissionService } from '@core/services/app-permission.service';
import { BetLimitGroupDataService } from '../services/bet-limit-group-data.service';
// Model
import { CanComponentDeactivate } from '@core/interfaces/can-deactivate.interface';
import {
  BetLimitCandidate, BetLimitCurrency, BetLimitGroupPayload, BetLimitNotAdded, BetLimitProviderOption, BetLimitSetting,
  betLimitCellError, betLimitCellKey, hasBetLimitValue
} from '@core/models/bet-limit-group.model';
// Third Party
import { forkJoin, of, Subscription } from 'rxjs';
import Swal from 'sweetalert2';

type SetupMode = 'create' | 'edit' | 'default';

const STEP_INFO = 0;
const STEP_LIMITS = 1;
const STEP_PLAYERS = 2;

/**
 * AIO-81599 — Members 1.8 Bet Limit: create / edit one sport bet limit group.
 *
 * Laid out like 3.2.2 Promotion Setup (step list left, step right, one Save). All state lives
 * here and is handed to the steps by reference; the steps stay mounted ([hidden]) so nothing is
 * lost moving between them. One request saves all three steps.
 *
 * Modes, from the query string: no id → create; the provider's default group → default (limits
 * only, no players); otherwise edit.
 */
@Component({
  selector: 'kt-bet-limit-setup',
  templateUrl: './bet-limit-setup.component.html',
  styleUrls: ['./bet-limit-setup.component.scss'],
})
export class BetLimitSetupComponent implements OnInit, OnDestroy, CanComponentDeactivate {

  readonly STEP_INFO = STEP_INFO;
  readonly STEP_LIMITS = STEP_LIMITS;
  readonly STEP_PLAYERS = STEP_PLAYERS;
  readonly stepLabels = ['Group Info', 'Bet Limits', 'Players'];

  mode: SetupMode = 'create';
  groupId: number = null;
  groupCode = '';
  membersCount = 0;

  loading = true;
  saving = false;
  currentStep = STEP_INFO;
  visited = new Set<number>();

  providers: BetLimitProviderOption[] = [];

  form: FormGroup;
  /** The group's own cells, keyed currency|sport|bet type. */
  cells = new Map<string, BetLimitSetting>();
  /** The provider default group's cells — the grey placeholders. */
  defaultCells = new Map<string, BetLimitSetting>();
  stagedAdd = new Map<number, BetLimitCandidate>();
  stagedRemove = new Set<number>();

  /** Bumped on every grid or player change so the steps' change detection can follow the maps. */
  revision = 0;
  /** Bumped when a group is (re)loaded, so the Players step fetches the saved list again. */
  loadCount = 0;

  // A save the operator may not make returns 403, and the error interceptor logs out on 403.
  canCreate = false;
  canEdit = false;

  private baseline = { info: '', cells: '' };
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private betLimitGroupDataService: BetLimitGroupDataService,
    private appPermissionService: AppPermissionService,
  ) { }

  ngOnInit() {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.maxLength(150)]),
      game_provider_id: new FormControl(null, Validators.required),
      status: new FormControl(1, Validators.required),
      remarks: new FormControl('', Validators.maxLength(250)),
    });

    this.subscriptions.add(this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canCreate = appPermissions.create_sport_bet_limit_group;
      this.canEdit = appPermissions.edit_sport_bet_limit_group;
      this.applyAccess();
    }));

    // A provider picked on Create: its own defaults, and no cells left from the previous one.
    this.subscriptions.add(this.form.get('game_provider_id').valueChanges.subscribe(() => {
      if (this.loading || this.isEditing) {
        return;
      }
      this.dropForeignCells();
      this.defaultCells = new Map();
      this.loadDefaults();
      this.revision++;
    }));

    // "Create New" only changes the query string, which reuses this component — so (re)load on
    // every change rather than once.
    this.subscriptions.add(this.route.queryParamMap.subscribe(params => {
      const id = params.get('id');
      const step = +params.get('step') || STEP_INFO;
      this.load(id ? +id : null, step);
    }));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /** Refresh or close the tab: the router guard does not run, so the browser asks instead. */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges && !this.saving) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  get provider(): BetLimitProviderOption {
    return this.providers.find(p => +p.game_provider_id === +this.form.get('game_provider_id').value) ?? null;
  }

  /** Only the currencies the provider is enabled for: GSYS refuses a template in any other. */
  get currencies(): BetLimitCurrency[] {
    return this.provider?.currencies ?? [];
  }

  get steps(): number[] {
    return this.mode === 'default' ? [STEP_INFO, STEP_LIMITS] : [STEP_INFO, STEP_LIMITS, STEP_PLAYERS];
  }

  get isEditing(): boolean {
    return this.mode !== 'create';
  }

  /** Create needs the create permission; edit (also of a default group) needs the edit one. */
  get canSave(): boolean {
    return this.isEditing ? this.canEdit : this.canCreate;
  }

  get readonly(): boolean {
    return !this.canSave;
  }

  get title(): string {
    if (this.mode === 'create') {
      return 'Create Sport Bet Limit Group';
    }
    return (this.mode === 'default' ? 'Edit Provider Default — ' : 'Edit Sport Bet Limit Group — ') + this.groupCode;
  }

  // ---------------------------------------------------------------- validity / dirty

  stepInvalid(step: number): boolean {
    if (step === STEP_INFO) {
      return this.mode !== 'default' && this.form.invalid;
    }
    if (step === STEP_LIMITS) {
      return this.invalidCellCount() > 0;
    }
    return false;
  }

  stepChanged(step: number): boolean {
    if (!this.isEditing) {
      return false;
    }
    if (step === STEP_INFO) {
      return this.snapshotInfo() !== this.baseline.info;
    }
    if (step === STEP_LIMITS) {
      return this.snapshotCells() !== this.baseline.cells;
    }
    return this.stagedAdd.size > 0 || this.stagedRemove.size > 0;
  }

  get hasUnsavedChanges(): boolean {
    if (this.loading) {
      return false;
    }
    if (!this.isEditing) {
      // A new group is "unsaved" as soon as anything is filled in.
      return !!this.form.get('name').value || this.cells.size > 0 || this.stagedAdd.size > 0;
    }
    return this.steps.some(step => this.stepChanged(step));
  }

  get anyInvalid(): boolean {
    return this.steps.some(step => this.stepInvalid(step));
  }

  stepDotClass(step: number): string {
    // Create: a step is only judged once the operator has been there and moved on.
    if (!this.isEditing && (!this.visited.has(step) || step === this.currentStep)) {
      return 'default-dot';
    }
    return this.stepInvalid(step) ? 'red-dot' : 'passed-dot';
  }

  invalidCellCount(): number {
    const provider = this.provider;
    if (!provider) {
      return 0;
    }
    let count = 0;
    this.cells.forEach(cell => count += betLimitCellError(cell, provider) ? 1 : 0);
    return count;
  }

  // ---------------------------------------------------------------- navigation

  onToStep(step: number) {
    this.visited.add(this.currentStep);
    this.currentStep = step;
    this.visited.add(step);
  }

  onBack() {
    const i = this.steps.indexOf(this.currentStep);
    if (i > 0) {
      this.onToStep(this.steps[i - 1]);
    }
  }

  onNext() {
    const i = this.steps.indexOf(this.currentStep);
    if (i < this.steps.length - 1) {
      this.onToStep(this.steps[i + 1]);
    }
  }

  get isLastStep(): boolean {
    return this.currentStep === this.steps[this.steps.length - 1];
  }

  onBackToList() {
    this.router.navigate(['/general/bet-limit']);
  }

  async onCreateNew() {
    // Only the query string changes, so the route guard does not run — ask here.
    if (await this.canDeactivate()) {
      this.router.navigate(['/general/bet-limit/setup']);
    }
  }

  onChanged() {
    this.revision++;
  }

  // ---------------------------------------------------------------- save

  onSubmit() {
    if (!this.canSave) {
      return;
    }
    if (this.anyInvalid) {
      this.form.markAllAsTouched();
      this.onToStep(this.steps.find(step => this.stepInvalid(step)));
      return;
    }

    const payload: BetLimitGroupPayload = {
      name: (this.form.get('name').value || '').trim(),
      status: +this.form.get('status').value,
      remarks: this.form.get('remarks').value || null,
      // A cell in a currency the provider does not support is dropped, so the save removes it.
      settings: Array.from(this.cells.values())
        .filter(cell => hasBetLimitValue(cell) && this.currencies.some(c => +c.id === +cell.settings_currency_id))
        .map(cell => ({
          ...cell,
          min_bet: cell.min_bet || null,
          max_bet: cell.max_bet || null,
          max_amount_per_match: cell.max_amount_per_match || null,
        })),
      members: { add: Array.from(this.stagedAdd.keys()), remove: Array.from(this.stagedRemove) },
    };

    this.saving = true;
    const request = this.isEditing
      ? this.betLimitGroupDataService.update(this.groupId, payload)
      : this.betLimitGroupDataService.create({ ...payload, game_provider_id: +this.form.get('game_provider_id').value });

    this.subscriptions.add(request.subscribe(
      res => {
        this.saving = false;
        const id = res.data?.id ?? this.groupId;
        const notAdded = res.data?.not_added ?? [];
        this.markClean();
        this.showSaveResult((res.message || []).join('<br>'), payload.members.add, notAdded).then(() => {
          // Players GSYS could not add: stay on Players so the operator can fix and add them again.
          if (notAdded.length) {
            if (this.isEditing) {
              this.load(id, STEP_PLAYERS);
            } else {
              this.router.navigate(['/general/bet-limit/setup'], { queryParams: { id, step: STEP_PLAYERS } });
            }
            return;
          }
          // Otherwise back to the list, as 3.2.2 Promotion Setup after a save. loading blanks the page
          // and makes hasUnsavedChanges false, so the route guard does not ask on a just-created group.
          this.loading = true;
          this.router.navigate(['/general/bet-limit']);
        });
      },
      // The api error interceptor has already shown the message; open the step at fault.
      error => {
        this.saving = false;
        const details = error?.details ?? {};
        // GSYS keeps one template per limit setting, so figures equal to the provider default are
        // refused. Empty cells already mean "use the default": clear them, the operator saves again.
        const sameAsDefault: number[] = details.same_as_default ?? [];
        if (sameAsDefault.length) {
          this.clearCurrencies(sameAsDefault);
          this.onToStep(STEP_LIMITS);
          return;
        }
        const keys = Object.keys(details);
        if (keys.some(k => k.startsWith('settings'))) {
          this.onToStep(STEP_LIMITS);
        } else if (keys.some(k => k.startsWith('members'))) {
          this.onToStep(STEP_PLAYERS);
        } else if (keys.length) {
          this.onToStep(STEP_INFO);
        }
      }
    ));
  }

  /**
   * The save message. When the save added players: an added / not added count, and the players
   * GSYS did not add in one box per reason, each with a button that copies its usernames.
   */
  private showSaveResult(message: string, sent: number[], notAdded: BetLimitNotAdded[]) {
    if (!sent.length && !notAdded.length) {
      return Swal.fire('System Message', message, 'success');
    }

    // A player already in the group can also be refused (e.g. the group was turned active).
    const sentIds = new Set(sent);
    const added = sent.length - notAdded.filter(row => sentIds.has(+row.member_account_id)).length;

    const tiles = this.el('div', 'display:grid;grid-template-columns:repeat(' + (sent.length ? 2 : 1) + ',minmax(0,1fr));gap:8px');
    if (sent.length) {
      tiles.appendChild(this.tile(`${added}`, `of ${sent.length} player(s) added`, '#bbf7d0', '#f0fdf4', '#15803d'));
    }
    tiles.appendChild(notAdded.length
      ? this.tile(`${notAdded.length}`, 'player(s) not added', '#fed7aa', '#fff7ed', '#b45309')
      : this.tile('0', 'player(s) not added', '#e4e4e7', '#fafafa', '#52525b'));

    const body = this.el('div', 'display:flex;flex-direction:column;gap:10px;margin-top:12px;text-align:left');
    body.appendChild(tiles);

    const byReason = new Map<string, string[]>();
    notAdded.forEach(row => byReason.set(row.reason, [...(byReason.get(row.reason) ?? []), row.username]));
    if (byReason.size) {
      body.appendChild(this.el('p', 'margin:0;font-size:13px;color:#71717a', 'Fix the reason below, then add these players again.'));
    }
    byReason.forEach((usernames, reason) => body.appendChild(this.reasonBox(reason, usernames)));

    return Swal.fire({
      title: 'System Message',
      icon: notAdded.length ? 'warning' : 'success',
      heightAuto: false,
      html: `<p class="m-0">${message}</p><div id="bet-limit-save-result"></div>`,
      didOpen: () => document.getElementById('bet-limit-save-result')?.appendChild(body),
    });
  }

  /** One reason, its player count, a copy button and the usernames, which scroll past 4 rows. */
  private reasonBox(reason: string, usernames: string[]): HTMLElement {
    const box = this.el('div', 'border:1px solid #e4e4e7;border-radius:6px;overflow:hidden');
    const header = this.el('div', 'display:flex;align-items:center;gap:8px;padding:8px 12px;background:#fafafa;border-bottom:1px solid #e4e4e7');
    header.appendChild(this.el('span', 'flex-grow:1;font-size:13px;font-weight:600;color:#27272a', reason));
    header.appendChild(this.el('span', 'font-size:12px;font-weight:600;color:#fff;background:#d97706;border-radius:10px;padding:1px 8px', `${usernames.length}`));

    // Usernames only, one per line, so they can be pasted into Add player.
    const copy = this.el('button', 'all:unset;cursor:pointer;padding:4px 6px;color:#52525b') as HTMLButtonElement;
    copy.type = 'button';
    copy.title = 'Copy usernames';
    copy.setAttribute('aria-label', 'Copy usernames');
    const icon = this.el('i', '');
    icon.className = 'fas fa-copy';
    copy.appendChild(icon);
    copy.onclick = () => navigator.clipboard.writeText(usernames.join('\n')).then(() => {
      icon.className = 'fas fa-check text-success';
      setTimeout(() => icon.className = 'fas fa-copy', 1500);
    }).catch(() => {});
    header.appendChild(copy);

    const list = this.el('div', 'display:flex;flex-wrap:wrap;gap:6px;padding:8px 12px 12px;max-height:120px;overflow-y:auto');
    usernames.forEach(username => list.appendChild(
      this.el('span', 'font-family:monospace;font-size:12px;color:#27272a;background:#f4f4f5;border-radius:4px;padding:3px 8px', username)
    ));

    box.appendChild(header);
    box.appendChild(list);
    return box;
  }

  private tile(count: string, label: string, border: string, background: string, color: string): HTMLElement {
    const tile = this.el('div', `display:flex;flex-direction:column;gap:2px;padding:8px 12px;border:1px solid ${border};border-radius:6px;background:${background}`);
    tile.appendChild(this.el('span', `font-size:22px;font-weight:600;color:${color}`, count));
    tile.appendChild(this.el('span', `font-size:12px;color:${color}`, label));
    return tile;
  }

  /** textContent, not innerHTML: usernames and GSYS messages are never parsed as HTML. */
  private el(tag: string, css: string, text?: string): HTMLElement {
    const node = document.createElement(tag);
    node.style.cssText = css;
    if (text !== undefined) {
      node.textContent = text;
    }
    return node;
  }

  /** UnSaveChangesGuard. */
  async canDeactivate(): Promise<boolean> {
    if (!this.hasUnsavedChanges || this.saving) {
      return true;
    }

    // Same dialog as Partnership and Banner: Discard to leave, Cancel to stay.
    const res = await Swal.fire({
      title: '<div class="text-center">Unsaved changes</div>',
      html: '<div class="text-center">You have unsaved changes. If you leave this page, they are lost.</div>',
      showDenyButton: true,
      showCloseButton: true,
      showCancelButton: false,
      showConfirmButton: true,
      reverseButtons: true,
      denyButtonText: 'Cancel',
      confirmButtonText: 'Discard',
      icon: 'warning',
      customClass: {
        denyButton: 'deny-button',
        confirmButton: 'confirm-button',
      },
    });

    return res.isConfirmed;
  }

  // ---------------------------------------------------------------- load

  private load(id: number | null, step: number) {
    this.loading = true;
    this.groupId = id;
    this.cells = new Map();
    this.defaultCells = new Map();
    this.stagedAdd = new Map();
    this.stagedRemove = new Set();
    this.visited = new Set();

    this.subscriptions.add(forkJoin([
      this.betLimitGroupDataService.getOptions(),
      id ? this.betLimitGroupDataService.getById(id) : of(null),
    ]).subscribe(([providers, detail]) => {
      this.providers = providers;

      if (detail) {
        const group = detail.group;
        this.mode = group.is_default ? 'default' : 'edit';
        this.groupCode = group.code;
        this.membersCount = detail.members_count;
        this.form.reset({ name: group.name, game_provider_id: group.game_provider_id, status: group.status, remarks: group.remarks ?? '' });
        detail.settings.forEach(cell => this.cells.set(this.key(cell), this.normalise(cell)));
      } else {
        this.mode = 'create';
        this.groupCode = '';
        this.membersCount = 0;
        this.form.reset({ name: '', game_provider_id: providers.length === 1 ? providers[0].game_provider_id : null, status: 1, remarks: '' });
      }

      this.markClean();
      this.loadDefaults();
      this.loadCount++;
      this.currentStep = this.steps.includes(step) ? step : STEP_INFO;
      this.visited.add(this.currentStep);
      this.loading = false;
      // Locks: the provider after create, a default group's info, everything without permission.
      this.applyAccess();
    }, () => this.loading = false));
  }

  /** Without the permission to save, every field is shown but locked. */
  private applyAccess() {
    if (this.loading || !this.form) {
      return;
    }
    if (this.readonly) {
      this.form.disable({ emitEvent: false });
      return;
    }
    // The same locks as load(): provider fixed after create, default group info fixed.
    ['name', 'status', 'remarks'].forEach(c => this.mode === 'default' ? this.form.get(c).disable({ emitEvent: false }) : this.form.get(c).enable({ emitEvent: false }));
    this.isEditing ? this.form.get('game_provider_id').disable({ emitEvent: false }) : this.form.get('game_provider_id').enable({ emitEvent: false });
  }

  private clearCurrencies(currencyIds: number[]) {
    const ids = new Set(currencyIds.map(id => +id));
    Array.from(this.cells.entries())
      .filter(([, cell]) => ids.has(+cell.settings_currency_id))
      .forEach(([key]) => this.cells.delete(key));
    this.revision++;
  }

  /** Cells whose sport the selected provider does not have; the api would reject them. */
  private dropForeignCells() {
    const sportIds = new Set((this.provider?.sports ?? []).map(s => +s.id));
    Array.from(this.cells.entries())
      .filter(([, cell]) => !sportIds.has(+cell.bet_limit_provider_sport_id))
      .forEach(([key]) => this.cells.delete(key));
  }

  /** The provider default's figures, for the grid placeholders and Copy from Default. */
  private loadDefaults() {
    const provider = this.provider;
    if (this.mode === 'default') {
      this.defaultCells = this.cells;
      return;
    }
    if (!provider?.default_group_id) {
      return;
    }
    this.subscriptions.add(this.betLimitGroupDataService.getById(provider.default_group_id).subscribe(detail => {
      const cells = new Map<string, BetLimitSetting>();
      detail.settings.forEach(cell => cells.set(this.key(cell), this.normalise(cell)));
      this.defaultCells = cells;
      this.revision++;
    }));
  }

  private markClean() {
    this.baseline = { info: this.snapshotInfo(), cells: this.snapshotCells() };
    this.stagedAdd.clear();
    this.stagedRemove.clear();
    this.revision++;
  }

  private snapshotInfo(): string {
    const v = this.form.getRawValue();
    return JSON.stringify([(v.name || '').trim(), +v.status, v.remarks || '']);
  }

  private snapshotCells(): string {
    return JSON.stringify(Array.from(this.cells.values())
      .filter(hasBetLimitValue)
      .map(c => [this.key(c), c.min_bet || null, c.max_bet || null, c.max_amount_per_match || null])
      .sort((a, b) => (a[0] as string).localeCompare(b[0] as string)));
  }

  private key(cell: BetLimitSetting): string {
    return betLimitCellKey(cell.settings_currency_id, cell.bet_limit_provider_sport_id, cell.bet_type_code);
  }

  /** The api returns decimal(30,8) strings; show them as typed (no trailing zeros). */
  private normalise(cell: BetLimitSetting): BetLimitSetting {
    const trim = (v: string | null) => v === null || v === undefined ? null : String(+v);
    return {
      settings_currency_id: +cell.settings_currency_id,
      bet_limit_provider_sport_id: +cell.bet_limit_provider_sport_id,
      bet_type_code: String(cell.bet_type_code),
      min_bet: trim(cell.min_bet),
      max_bet: trim(cell.max_bet),
      max_amount_per_match: trim(cell.max_amount_per_match),
    };
  }
}
